import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';

import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { SharedLanguageService } from '@language';
import { ColumnDef } from '../../../overviews/list-view/entities/column-definition.entity';

import { LabelType } from '../../../elements/components/label/entities/label-type.entity';
import { AsyncStatusCell } from '../../../overviews/list-view/components/cells/async-status-cell/async-status-cell.entity';
import { AsyncTextCell } from '../../../overviews/list-view/components/cells/async-text-cell/async-text-cell.entity';
import { TextCell } from '../../../overviews/list-view/components/cells/text-cell/text-cell.entity';
import { ColumnWidth } from '../../../overviews/list-view/entities/column-width.entity';
import { JobStatus, PaginatedBatch } from '../entities/batch-object.entity';
import { ShBatchTaskTypes } from '../entities/batch-options.class';

export type BatchColumn = ColumnDef<typeof BatchColumnService, PaginatedBatch>;

@Injectable({ providedIn: 'root' })
export class BatchColumnService {
    static NAME: BatchColumn;
    static LABEL: BatchColumn;
    static COMMENCEMENT: BatchColumn;
    static FINALISATION: BatchColumn;
    static COMPLETED: BatchColumn;
    static SKIPPED: BatchColumn;
    static STATUS: BatchColumn;
    static USER: BatchColumn;
    static REMARK: BatchColumn;

    constructor(
        private readonly translateService: SharedLanguageService,
        private readonly datePipe: DatePipe
    ) {
        BatchColumnService.NAME = new ColumnDef({
            key: 'NAME',
            translateLabel: `batches.field-keys.name`,
            mapper: batch => new TextCell(batch.Label)
        });

        BatchColumnService.LABEL = new ColumnDef({
            key: 'LABEL',
            translateLabel: `batches.field-keys.label`,
            mapper: batch => new TextCell(batch.Tag)
        });

        BatchColumnService.COMMENCEMENT = new ColumnDef({
            key: 'COMMENCEMENT',
            translateLabel: `batches.field-keys.commencement`,
            mapper: batch => new TextCell(this.datePipe.transform(batch.StartDate, 'short'))
        });

        BatchColumnService.FINALISATION = new ColumnDef({
            key: 'FINALISATION',
            translateLabel: `batches.field-keys.finalisation`,
            mapper: batch => new TextCell(this.datePipe.transform(batch.FinishDate, 'short'))
        });

        BatchColumnService.COMPLETED = new ColumnDef({
            key: 'COMPLETED',
            translateLabel: `batches.field-keys.completed`,
            mapper: batch => new TextCell(batch.Completed.toString()),
            width: ColumnWidth.SMALL
        });

        BatchColumnService.SKIPPED = new ColumnDef({
            key: 'SKIPPED',
            translateLabel: `batches.field-keys.skipped`,
            mapper: batch => new TextCell(batch.Skipped.toString()),
            width: ColumnWidth.SMALL
        });

        BatchColumnService.REMARK = new ColumnDef({
            key: 'REMARK',
            translateLabel: 'batches.field-keys.remark',
            mapper: batch =>
                new TextCell(
                    batch.TaskType === ShBatchTaskTypes.PUBLISH && batch.Skipped > 0
                        ? this.translateService.instant('batches.remarks.publish')
                        : '-'
                )
        });

        BatchColumnService.STATUS = new ColumnDef({
            key: 'STATUS',
            translateLabel: `batches.field-keys.status`,
            mapper: batch =>
                new AsyncStatusCell(
                    combineLatest([batch.Status$, batch.LabelType$]).pipe(
                        map(([status, labelType]) => ({ status, labelType }))
                    ),
                    data =>
                        data.status === JobStatus.IN_PROGRESS
                            ? `${Math.round(batch.Progress)}%`
                            : this.translateService.instant(`batches.status.${data.status}`),
                    data => data.labelType,
                    data => data.status === JobStatus.IN_PROGRESS,
                    batch.Progress,
                    this.translateService.instant('ui.loading'),
                    LabelType.Update
                ),
            width: ColumnWidth.SMALL
        });

        BatchColumnService.USER = new ColumnDef({
            key: 'USER',
            translateLabel: `batches.field-keys.user`,
            mapper: batch =>
                new AsyncTextCell(
                    batch.User$,
                    user => `${user?.FirstName} ${user?.LastName} <br/> (${user?.Login})`,
                    this.translateService.instant('ui.loading')
                )
        });
    }
}
